import { cloneElement, useId, type ReactElement, type ReactNode } from 'react';
import { ApiError } from '../api';
import { useTip } from './HelpTip';

// A labelled control with an optional hint and error (the UI library's field look), wired with aria-describedby
// and aria-invalid. The child is the input, select, or textarea itself. help is a
// tip that opens below the field while its label is hovered or the control has
// focus, and describes the control.
export function Field({ label, hint, help, error, required, className, children }: {
  label: ReactNode; hint?: ReactNode; help?: ReactNode; error?: string | null; required?: boolean; className?: string;
  children: ReactElement<Record<string, unknown>>;
}) {
  const id = useId();
  const tip = useTip();
  const controlId = (children.props.id as string | undefined) ?? `${id}-c`;
  const hintId = hint ? `${id}-h` : undefined;
  const errId = error ? `${id}-e` : undefined;
  const describedBy = [hintId, errId, help ? tip.id : undefined].filter(Boolean).join(' ') || undefined;
  return (
    <div className={`field ${help ? 'helptip ' : ''}${className ?? ''}`} {...(help ? tip.scope : {})}>
      <label htmlFor={controlId} className={`text-sm font-normal text-foreground${help ? ' helptip-trigger' : ''}`}>{label}{required && <span className="text-muted"> (required)</span>}</label>
      {cloneElement(children, { id: controlId, 'aria-describedby': describedBy, 'aria-invalid': error ? true : undefined, required: required || undefined })}
      {hint && <div id={hintId} className="text-sm text-muted">{hint}</div>}
      {error && <div id={errId} className="text-sm font-medium text-danger">{error}</div>}
      {help && tip.text(help)}
    </div>
  );
}

// Maps the gateway's schema errors ("/emails_add/0/address") onto form field keys.
export function fieldErrors(error: unknown, map: (segments: string[]) => string | undefined): Record<string, string> {
  const out: Record<string, string> = {};
  if (!(error instanceof ApiError) || !Array.isArray(error.details)) return out;
  for (const d of error.details as Array<{ path: string; message: string }>) {
    const key = map(d.path.split('/').filter((x) => x && !/^\d+$/.test(x)));
    if (key && !out[key]) out[key] = d.message;
  }
  return out;
}
